import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import { getStagedVoyages } from '../services/StagingRepository';
import { getLpSuppliers } from '../../master-data';
import useDateFormat from '../../../shared/hooks/useDateFormat';
import useUserRoles from '../../../shared/hooks/useUserRoles';
import StagingReviewModal from './StagingReviewModal';
import ExpSnackbarAlerts from '../../../shared/components/display/ExpSnackbarAlerts';
import { confidenceColor } from '../utils/stagingConstants';

const DEFAULT_PAGE_SIZE = 50;

const StagingArea = ({ onResolved }) => {
  const [stagedVoyages, setStagedVoyages] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [loading, setLoading] = useState(false);
  const [selectedStaged, setSelectedStaged] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const { formatDateTime } = useDateFormat();
  const { hasUnrestrictedAccess } = useUserRoles();
  const [portOperators, setPortOperators] = useState([]);
  const [selectedPortOperator, setSelectedPortOperator] = useState('');

  const fetchData = useCallback(async (portOperatorId, page, pageSize) => {
    setLoading(true);
    try {
      const response = await getStagedVoyages(portOperatorId || undefined, {
        page,
        size: pageSize,
      });
      let content = response?.content || [];
      const totalElements = response?.totalElements ?? content.length;

      // Resolving the last item on a page can leave it empty — fall back a page.
      if (content.length === 0 && page > 0 && totalElements > 0) {
        setPaginationModel((prev) => ({ ...prev, page: page - 1 }));
        return;
      }

      setStagedVoyages(content);
      setRowCount(totalElements);
    } catch (error) {
      setStagedVoyages([]);
      setRowCount(0);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load staged voyages',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(
      selectedPortOperator,
      paginationModel.page,
      paginationModel.pageSize
    );
  }, [fetchData, selectedPortOperator, paginationModel]);

  useEffect(() => {
    if (hasUnrestrictedAccess) {
      getLpSuppliers({ isPortOperator: true })
        .then((data) => {
          setPortOperators(data || []);
        })
        .catch(() => setPortOperators([]));
    }
  }, [hasUnrestrictedAccess]);

  const handleReviewClick = (row) => {
    setSelectedStaged(row);
    setReviewOpen(true);
  };

  const handleReviewClose = () => {
    setReviewOpen(false);
    setSelectedStaged(null);
  };

  const refetch = () =>
    fetchData(
      selectedPortOperator,
      paginationModel.page,
      paginationModel.pageSize
    );

  const handleResolved = () => {
    refetch();
    if (onResolved) onResolved();
  };

  const columns = [
    {
      field: 'stagedAt',
      headerName: 'Staged At',
      width: 160,
      renderCell: (params) => formatDateTime(params.value),
    },
    { field: 'voyageNumber', headerName: 'Voyage Number', width: 140 },
    {
      field: 'sourceMethod',
      headerName: 'Method',
      width: 90,
      renderCell: (params) => {
        const method = params.value || 'POST';
        const colors = { POST: '#2e7d32', PUT: '#1565c0' };
        return (
          <Chip
            size='small'
            label={method}
            sx={{
              backgroundColor: colors[method] || '#757575',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              minWidth: 56,
            }}
          />
        );
      },
    },
    {
      field: 'shipInfo',
      headerName: 'Ship',
      width: 180,
      valueGetter: (value, row) => {
        const { imo, shipName } = row;
        return shipName ? `${shipName} (${imo})` : imo;
      },
    },
    {
      field: 'route',
      headerName: 'Route',
      width: 160,
      valueGetter: (value, row) =>
        `${row.departurePort} → ${row.destinationPort}`,
    },
    {
      field: 'actualETD',
      headerName: 'ETD',
      width: 140,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'actualETA',
      headerName: 'ETA',
      width: 140,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'portOperatorName',
      headerName: 'Port Operator',
      width: 160,
    },
    {
      field: 'matchConfidence',
      headerName: 'Match Confidence',
      width: 150,
      renderCell: (params) => {
        const val = params.value;
        if (val == null)
          return <Chip label='No Match' size='small' variant='outlined' />;
        const pct = Math.round(val * 100);
        return (
          <Chip label={`${pct}%`} size='small' color={confidenceColor(val)} />
        );
      },
    },
    {
      field: 'suggestedVoyageNumber',
      headerName: 'Suggested Match',
      width: 150,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant='outlined'
          size='small'
          onClick={() => handleReviewClick(params.row)}
        >
          Review
        </Button>
      ),
    },
  ];

  if (!loading && rowCount === 0) {
    return (
      <Box>
        {hasUnrestrictedAccess && portOperators.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <FormControl size='small' sx={{ minWidth: 250 }}>
              <InputLabel>Filter by Port Operator</InputLabel>
              <Select
                value={selectedPortOperator}
                label='Filter by Port Operator'
                onChange={(e) => {
                  setSelectedPortOperator(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value=''>All Port Operators</MenuItem>
                {portOperators.map((po) => (
                  <MenuItem key={po.supplierNo} value={po.supplierNo}>
                    {po.supplierName} ({po.supplierNo})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <InboxIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant='h6' color='text.secondary'>
            No staged voyages pending review
          </Typography>
          <Typography variant='body2' color='text.disabled' sx={{ mt: 1 }}>
            Voyages received via the API will appear here for matching before
            being added to the system.
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={refetch} sx={{ mt: 3 }}>
            Refresh
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant='h6'>
            Staging Area ({rowCount} pending)
          </Typography>
          {hasUnrestrictedAccess && portOperators.length > 0 && (
            <FormControl size='small' sx={{ minWidth: 250 }}>
              <InputLabel>Filter by Port Operator</InputLabel>
              <Select
                value={selectedPortOperator}
                label='Filter by Port Operator'
                onChange={(e) => {
                  setSelectedPortOperator(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value=''>All Port Operators</MenuItem>
                {portOperators.map((po) => (
                  <MenuItem key={po.supplierNo} value={po.supplierNo}>
                    {po.supplierName} ({po.supplierNo})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Tooltip title='Refresh'>
          <span>
            <IconButton onClick={refetch} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Paper sx={{ width: '100%' }}>
        <DataGridPro
          rows={stagedVoyages}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          paginationMode='server'
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
        />
      </Paper>
      {selectedStaged && (
        <StagingReviewModal
          open={reviewOpen}
          onClose={handleReviewClose}
          stagedVoyage={selectedStaged}
          onResolved={handleResolved}
        />
      )}
      <ExpSnackbarAlerts
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};

export default StagingArea;
